import React, { useState, useEffect, useContext, useRef } from 'react'
import { useAuth } from './context/AuthContext.jsx'
import { migrateDefaultUser } from './utils/migrateDefaultUser.js'
import AuthPage from './pages/Auth/AuthPage.jsx'
import AccountSettings from './pages/Auth/AccountSettings.jsx'
import Nav from './components/Nav.jsx'
import Modal from './components/Modal.jsx'
import BackgroundScene from './components/BackgroundScene.jsx'
import Today from './pages/Today/Today.jsx'
import Wellbeing from './pages/Wellbeing/Wellbeing.jsx'
import MySpace from './pages/MySpace/MySpace.jsx'
import ChallengesPage from './pages/Challenges/ChallengesPage.jsx'
import Reading from './pages/Reading/Reading.jsx'
import Writing from './pages/Writing/Writing.jsx'
import Gardening from './pages/Gardening/Gardening.jsx'
import Cooking from './pages/Cooking/Cooking.jsx'
import Journaling from './pages/Journaling/Journaling.jsx'
import Goals from './pages/Goals/Goals.jsx'
import Learning from './pages/Learning/Learning.jsx'
import { useTheme } from './hooks/useTheme.js'
import { ThemeContext } from './context/ThemeContext.jsx'

// Normalize legacy page IDs to new IDs so old internal calls still work
const LEGACY_REDIRECTS = {
  'daily-summary': 'dashboard',
  'today':         'dashboard',
  'check-in':      'dashboard',
  'activities':    'challenges',
  'gardening':     'hobbies/gardening',
  'cooking':       'hobbies/cooking',
  'reading':       'hobbies/reading',
  'journaling':    'hobbies/journaling',
  'writing':       'hobbies/writing',
  'goals':         'my-space/goals',
}

function normalizePage(page) {
  return LEGACY_REDIRECTS[page] ?? page
}

export default function App() {
  const { user, loading } = useAuth()
  const [activePage, setActivePage]   = useState('dashboard')
  const [captureOpen, setCaptureOpen] = useState(false)
  const { applyTheme } = useTheme()
  const { theme } = useContext(ThemeContext)
  const migratedRef = useRef(false)

  useEffect(() => {
    applyTheme(activePage)
    Object.entries(theme.cssVars).forEach(([k, v]) =>
      document.documentElement.style.setProperty(k, v)
    )
    window.scrollTo(0, 0)
  }, [activePage, theme])

  // Run once when user first becomes authenticated
  useEffect(() => {
    if (user && !migratedRef.current) {
      migratedRef.current = true
      migrateDefaultUser(user.id)
    }
  }, [user])

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: 'var(--bg)',
        fontFamily: 'var(--font-serif)', color: 'var(--stone)',
        fontSize: '1rem', fontStyle: 'italic',
      }}>
        Loading…
      </div>
    )
  }

  if (!user) return <AuthPage />

  function navigateTo(page) { setActivePage(normalizePage(page)) }

  function renderPage() {
    switch (activePage) {
      case 'dashboard':
        return <Today onNavigate={navigateTo} />

      case 'wellbeing':
        return <Wellbeing initialSection="mood" />
      case 'wellbeing/mood':
        return <Wellbeing initialSection="mood" />
      case 'wellbeing/activity':
        return <Wellbeing initialSection="activity" />
      case 'wellbeing/trends':
        return <Wellbeing initialSection="trends" />
      case 'wellbeing/reflections':
        return <Wellbeing initialSection="reflections" />

      case 'hobbies/gardening':
        return <Gardening />
      case 'hobbies/cooking':
        return <Cooking />
      case 'hobbies/writing':
        return <Writing />
      case 'hobbies/reading':
        return <Reading />
      case 'hobbies/journaling':
        return <Journaling />

      case 'challenges':
        return <ChallengesPage initialSection="today" />
      case 'challenges/active':
        return <ChallengesPage initialSection="active" />
      case 'challenges/explore':
        return <ChallengesPage initialSection="explore" />
      case 'challenges/vault':
        return <ChallengesPage initialSection="vault" />
      case 'challenges/completed':
        return <ChallengesPage initialSection="completed" />

      case 'my-space':
      case 'my-space/pages':
      case 'my-space/trackers':
        return <MySpace />

      case 'my-space/goals':
        return <Goals />

      case 'account-settings':
        return <AccountSettings onNavigate={navigateTo} />

      case 'learning':
        return <Learning />

      default:
        return <Today />
    }
  }

  return (
    <>
      <BackgroundScene />
      <Nav
        activePage={activePage}
        onNavigate={navigateTo}
        onCapture={() => setCaptureOpen(true)}
      />
      <Modal isOpen={captureOpen} onClose={() => setCaptureOpen(false)} />
      <main>
        {renderPage()}
      </main>
    </>
  )
}
